import {
  formatPrepaidPurchaseItemDisplayName,
  formatPrepaidWashTypeLabel,
  PrepaidPurchaseRedemption,
  PrepaidPurchaseStatus,
  PrepaidPurchaseStatusItem,
  PrepaidRedemptionItemPayload
} from './prepaid-wash-book.models';

export type PrepaidRedemptionSlotTab = 'active' | 'redeemed';

/** One redeemable wash slot expanded from a purchase status line item. */
export interface PrepaidRedemptionSlot {
  slotKey: string;
  slotIndex: number;
  label: string;
  tab: PrepaidRedemptionSlotTab;
  purchaseItemId: string;
  packageId: string;
  type: string;
  expiresAt: string | null;
  packageName: string;
}

export function buildPrepaidRedemptionSlotKey(purchaseItemId: string, slotIndex: number): string {
  return `${purchaseItemId}#${slotIndex}`;
}

export function parsePrepaidRedemptionSlotKey(
  slotKey: string
): { purchaseItemId: string; slotIndex: number } | null {
  const idx = slotKey.lastIndexOf('#');
  if (idx <= 0) {
    return null;
  }
  const purchaseItemId = slotKey.slice(0, idx).trim();
  const slotIndex = Number(slotKey.slice(idx + 1));
  if (!purchaseItemId || !Number.isFinite(slotIndex) || slotIndex < 1) {
    return null;
  }
  return { purchaseItemId, slotIndex };
}

export function resolvePrepaidPurchaseItemType(item: PrepaidPurchaseStatusItem): string {
  if (item.type === 'add_on' || item.is_add_on) {
    return 'add_on';
  }
  return item.type || 'single_wash';
}

export function getPrepaidPurchaseItemLimit(item: PrepaidPurchaseStatusItem): number {
  const n = item.wash_limit ?? item.limit ?? 0;
  return Math.max(0, Math.floor(Number(n)) || 0);
}

/** Expands a status line into per-wash redemption slots (Figma: Wash 1, Wash 2, …). */
export function expandPrepaidPurchaseItemsToRedemptionSlots(
  item: PrepaidPurchaseStatusItem
): PrepaidRedemptionSlot[] {
  const total = getPrepaidPurchaseItemLimit(item);
  if (total <= 0) {
    return [];
  }

  const redeemed = Math.max(
    0,
    Math.min(total, Math.floor(Number(item.redeemed_count)) || 0)
  );
  const purchaseItemId = (item.purchase_item_id || item.id || '').trim();
  if (!purchaseItemId) {
    return [];
  }

  const type = resolvePrepaidPurchaseItemType(item);
  const packageName = item.package_name?.trim() || 'Wash';
  const expiresAt = (item.expires_at ?? '').trim() || null;
  const slots: PrepaidRedemptionSlot[] = [];

  for (let slotIndex = 1; slotIndex <= total; slotIndex++) {
    if (slotIndex <= redeemed) {
      continue;
    }
    if (item.is_expired) {
      continue;
    }
    const label =
      type === 'single_wash' ? `${packageName} | Wash ${slotIndex}` : packageName;

    slots.push({
      slotKey: buildPrepaidRedemptionSlotKey(purchaseItemId, slotIndex),
      slotIndex,
      label,
      tab: 'active',
      purchaseItemId,
      packageId: item.package_id,
      type,
      expiresAt,
      packageName
    });
  }

  return slots;
}

export function mapPrepaidPurchaseRedemptionsToSlots(
  redemptions: PrepaidPurchaseRedemption[] | null | undefined
): PrepaidRedemptionSlot[] {
  if (!redemptions?.length) {
    return [];
  }

  const byItem = new Map<string, PrepaidPurchaseRedemption[]>();
  for (const redemption of redemptions) {
    const itemId = (redemption.prepaid_purchase_item_id || redemption.item?.purchase_item_id || '').trim();
    if (!itemId) {
      continue;
    }
    const list = byItem.get(itemId) ?? [];
    list.push(redemption);
    byItem.set(itemId, list);
  }

  const slots: PrepaidRedemptionSlot[] = [];
  for (const [, group] of byItem) {
    const sorted = [...group].sort(
      (a, b) => new Date(a.redeemed_at).getTime() - new Date(b.redeemed_at).getTime()
    );
    sorted.forEach((redemption, index) => {
      const item = redemption.item;
      const purchaseItemId = (
        redemption.prepaid_purchase_item_id ||
        item?.purchase_item_id ||
        item?.id ||
        ''
      ).trim();
      if (!purchaseItemId || !redemption.id?.trim()) {
        return;
      }
      const type =
        item?.type === 'add_on' || item?.is_add_on ? 'add_on' : item?.type || 'single_wash';
      const packageName = item?.package_name?.trim() || 'Wash';
      const expiresAt = (item?.expires_at ?? '').trim() || null;
      const slotIndex = index + 1;
      const label =
        type === 'single_wash' ? `${packageName} | Wash ${slotIndex}` : packageName;

      slots.push({
        slotKey: redemption.id.trim(),
        slotIndex,
        label,
        tab: 'redeemed',
        purchaseItemId,
        packageId: item?.package_id ?? '',
        type,
        expiresAt,
        packageName
      });
    });
  }

  return slots.sort(
    (a, b) =>
      a.purchaseItemId.localeCompare(b.purchaseItemId) || a.slotIndex - b.slotIndex
  );
}

export function expandPrepaidPurchaseStatusToRedemptionSlots(
  status: PrepaidPurchaseStatus | null | undefined
): PrepaidRedemptionSlot[] {
  if (!status?.items?.length) {
    return [];
  }
  const sorted = [...status.items].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
  );
  return sorted.flatMap((item) => expandPrepaidPurchaseItemsToRedemptionSlots(item));
}

/** Short expiry for redemption list subline, e.g. "Oct 22, 2026". */
export function formatPrepaidPurchaseExpiry(iso: string | null | undefined): string {
  const raw = (iso ?? '').trim();
  if (!raw) {
    return '';
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) {
    return raw;
  }
  const month = d.toLocaleString('en-US', { month: 'short' });
  return `${month} ${d.getDate()}, ${d.getFullYear()}`;
}

/** Long date for success modal, e.g. "Thu Dec 20, 2025". */
export function formatPrepaidPurchaseDateLong(iso: string | null | undefined): string {
  const raw = (iso ?? '').trim();
  if (!raw) {
    return '';
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) {
    return raw;
  }
  const weekday = d.toLocaleString('en-US', { weekday: 'short' });
  const month = d.toLocaleString('en-US', { month: 'short' });
  return `${weekday} ${month} ${d.getDate()}, ${d.getFullYear()}`;
}

/** Display fields for post-redemption success modal. */
export interface PrepaidRedeemedSuccessDisplay {
  packageLabel: string;
  washName: string;
  washType: string;
  purchaseDate: string;
  validTill: string;
}

function uniqueAndJoined(values: Iterable<string>): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    const trimmed = String(v ?? '').trim();
    if (!trimmed || seen.has(trimmed)) {
      continue;
    }
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out.join(' and ');
}

function resolveSelectedRedemptionSlots(
  status: PrepaidPurchaseStatus | null | undefined,
  selectedSlotKeys: Iterable<string>
): PrepaidRedemptionSlot[] {
  const allSlots = expandPrepaidPurchaseStatusToRedemptionSlots(status);
  const byKey = new Map(allSlots.map((s) => [s.slotKey, s]));
  const selected: PrepaidRedemptionSlot[] = [];
  const seen = new Set<string>();
  for (const rawKey of selectedSlotKeys) {
    const key = String(rawKey).trim();
    if (!key || seen.has(key)) {
      continue;
    }
    seen.add(key);
    const slot = byKey.get(key);
    if (slot) {
      selected.push(slot);
    }
  }
  return selected;
}

/** Builds prepaid redemption success modal copy from status + selected slot keys. */
export function buildPrepaidRedeemedSuccessDisplay(
  status: PrepaidPurchaseStatus | null | undefined,
  selectedSlotKeys: Iterable<string>
): PrepaidRedeemedSuccessDisplay {
  const selected = resolveSelectedRedemptionSlots(status, selectedSlotKeys);
  const packageLabel = status?.book_name?.trim() || 'Prepaid Washbook';
  const washName = uniqueAndJoined(selected.map((s) => s.packageName));
  const washType = uniqueAndJoined(
    selected.map((s) => formatPrepaidWashTypeLabel(s.type))
  );
  const purchaseDate =
    formatPrepaidPurchaseDateLong(status?.purchase_created_at) ||
    formatPrepaidPurchaseDateLong(status?.order_created_at) ||
    '—';
  const validTill = uniqueAndJoined(
    selected.map((s) => formatPrepaidPurchaseDateLong(s.expiresAt)).filter(Boolean)
  );

  return {
    packageLabel,
    washName: washName || '—',
    washType: washType || '—',
    purchaseDate,
    validTill: validTill || '—'
  };
}

/** Subline under a redemption row: "Single | Expires Oct 22, 2026". */
export function formatPrepaidRedemptionSlotSubline(slot: PrepaidRedemptionSlot): string {
  const typeLabel = formatPrepaidWashTypeLabel(slot.type);
  const expiry = formatPrepaidPurchaseExpiry(slot.expiresAt);
  if (expiry) {
    return `${typeLabel} | Expires ${expiry}`;
  }
  return typeLabel;
}

/** Maps selected slot keys to `POST /order` `redemption_items`. */
export function buildPrepaidOrderRedemptionItemsFromSlotKeys(
  status: PrepaidPurchaseStatus | null | undefined,
  slotKeys: Iterable<string>
): { purchase_item_id: string; package_id: string; count: number }[] {
  const purchaseItemToPackageId = new Map<string, string>();
  for (const line of status?.items ?? []) {
    const purchaseItemId = (line.purchase_item_id || line.id || '').trim();
    const packageId = (line.package_id || '').trim();
    if (purchaseItemId && packageId) {
      purchaseItemToPackageId.set(purchaseItemId, packageId);
    }
  }

  const countByPurchaseItemId = new Map<string, number>();
  const seen = new Set<string>();
  for (const rawKey of slotKeys) {
    const parsed = parsePrepaidRedemptionSlotKey(String(rawKey).trim());
    if (!parsed) {
      continue;
    }
    const dedupe = `${parsed.purchaseItemId}#${parsed.slotIndex}`;
    if (seen.has(dedupe)) {
      continue;
    }
    seen.add(dedupe);
    if (!purchaseItemToPackageId.has(parsed.purchaseItemId)) {
      continue;
    }
    countByPurchaseItemId.set(
      parsed.purchaseItemId,
      (countByPurchaseItemId.get(parsed.purchaseItemId) ?? 0) + 1
    );
  }

  return [...countByPurchaseItemId.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([purchase_item_id, count]) => ({
      purchase_item_id,
      package_id: purchaseItemToPackageId.get(purchase_item_id) ?? '',
      count
    }))
    .filter((line) => line.package_id);
}

/** Maps selected slot keys (`purchaseItemId#washIndex`) to dedicated redeem API line items. */
export function buildPrepaidRedemptionItemsFromSlotKeys(
  slotKeys: Iterable<string>
): PrepaidRedemptionItemPayload[] {
  const items: PrepaidRedemptionItemPayload[] = [];
  const seen = new Set<string>();
  for (const rawKey of slotKeys) {
    const parsed = parsePrepaidRedemptionSlotKey(String(rawKey).trim());
    if (!parsed) {
      continue;
    }
    const dedupe = `${parsed.purchaseItemId}#${parsed.slotIndex}`;
    if (seen.has(dedupe)) {
      continue;
    }
    seen.add(dedupe);
    items.push({
      purchase_item_id: parsed.purchaseItemId,
      wash_index: parsed.slotIndex
    });
  }
  return items.sort(
    (a, b) =>
      a.purchase_item_id.localeCompare(b.purchase_item_id) || a.wash_index - b.wash_index
  );
}

export function togglePrepaidRedemptionSlotKey(
  selectedKeys: string[],
  slotKey: string,
  checked: boolean
): string[] {
  const key = slotKey.trim();
  if (!key) {
    return selectedKeys;
  }
  const set = new Set(selectedKeys);
  if (checked) {
    set.add(key);
  } else {
    set.delete(key);
  }
  return [...set].sort();
}

export function hasActivePrepaidPurchaseRemaining(status: PrepaidPurchaseStatus | null): boolean {
  if (!status?.items?.length) {
    return false;
  }
  return status.items.some((item) => !item.is_expired && (item.remaining ?? 0) > 0);
}

export type PrepaidPurchaseStatusBadge = 'active' | 'expired';

/** Active when any line is not expired; expired when every line is expired. */
export function getPrepaidPurchaseStatusBadge(
  status: PrepaidPurchaseStatus | null
): PrepaidPurchaseStatusBadge | null {
  if (!status?.items?.length) {
    return null;
  }
  if (status.items.every((item) => item.is_expired)) {
    return 'expired';
  }
  return 'active';
}

/** True when every non-expired line has no washes left to redeem. */
export function isPrepaidPurchaseFullyRedeemed(status: PrepaidPurchaseStatus | null): boolean {
  if (!status?.items?.length) {
    return false;
  }
  if (status.items.every((item) => item.is_expired)) {
    return false;
  }
  const nonExpiredItems = status.items.filter((item) => !item.is_expired);
  if (nonExpiredItems.length === 0) {
    return false;
  }
  return nonExpiredItems.every((item) => (item.remaining ?? 0) <= 0);
}

export interface PrepaidPurchaseStatusSummaryCard {
  purchaseItemId: string;
  washTypeLabel: string;
  remaining: number;
  limit: number;
  packageLabel: string;
  dateLine: string;
}

function formatPrepaidPurchaseStatusSummaryDateLine(
  status: PrepaidPurchaseStatus,
  item: PrepaidPurchaseStatusItem
): string {
  const purchased = formatPrepaidPurchaseExpiry(
    status.purchase_created_at || status.order_created_at
  );
  const expires = formatPrepaidPurchaseExpiry(item.expires_at);
  const segments: string[] = [];
  if (purchased) {
    segments.push(`Purchased: ${purchased}`);
  }
  if (expires) {
    segments.push(`Expires ${expires}`);
  }
  return segments.join(' ');
}

export function getPrepaidPurchaseStatusSummaryCards(
  status: PrepaidPurchaseStatus | null
): PrepaidPurchaseStatusSummaryCard[] {
  if (!status?.items?.length) {
    return [];
  }
  return [...status.items]
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((item) => {
      const limit = getPrepaidPurchaseItemLimit(item);
      const remaining = Math.max(0, Math.floor(Number(item.remaining)) || 0);
      const purchaseItemId = (item.purchase_item_id || item.id || '').trim();
      return {
        purchaseItemId,
        washTypeLabel: formatPrepaidWashTypeLabel(resolvePrepaidPurchaseItemType(item)),
        remaining,
        limit,
        packageLabel: formatPrepaidPurchaseItemDisplayName(item),
        dateLine: formatPrepaidPurchaseStatusSummaryDateLine(status, item)
      };
    });
}
